/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { computed, Injectable, signal } from '@angular/core';

export type TourPlacement = 'right' | 'bottom' | 'left';

export interface TourStep {
  id: string;
  /** What the step points at; a step without one is shown in the middle of the screen. */
  target?: string;
  placement?: TourPlacement;
  icon: string;
  titleKey: string;
  bodyKey: string;
}

/**
 * The tour, in the order a new person meets the app. Sidebar entries are found
 * by the extension id ACA renders as `data-automation-id`, so a menu hidden by
 * a policy or by the workflow switch drops its step instead of pointing at
 * nothing.
 */
export const TOUR_STEPS: TourStep[] = [
  { id: 'welcome', icon: 'waving_hand', titleKey: 'DMS.TOUR.WELCOME_TITLE', bodyKey: 'DMS.TOUR.WELCOME_BODY' },
  {
    id: 'upload',
    target: '[data-automation-id="app.navbar.documents.upload"]',
    placement: 'right',
    icon: 'cloud_upload',
    titleKey: 'DMS.TOUR.UPLOAD_TITLE',
    bodyKey: 'DMS.TOUR.UPLOAD_BODY'
  },
  {
    id: 'documents',
    target: '[data-automation-id="app.navbar.documents.all"]',
    placement: 'right',
    icon: 'description',
    titleKey: 'DMS.TOUR.DOCUMENTS_TITLE',
    bodyKey: 'DMS.TOUR.DOCUMENTS_BODY'
  },
  {
    id: 'search',
    target: '.aca-shell-header__search',
    placement: 'bottom',
    icon: 'search',
    titleKey: 'DMS.TOUR.SEARCH_TITLE',
    bodyKey: 'DMS.TOUR.SEARCH_BODY'
  },
  { id: 'palette', icon: 'keyboard_command_key', titleKey: 'DMS.TOUR.PALETTE_TITLE', bodyKey: 'DMS.TOUR.PALETTE_BODY' },
  {
    id: 'work',
    target: '[data-automation-id="app.navbar.documents.work"]',
    placement: 'right',
    icon: 'inbox',
    titleKey: 'DMS.TOUR.WORK_TITLE',
    bodyKey: 'DMS.TOUR.WORK_BODY'
  },
  {
    id: 'assistant',
    target: '.aca-ai-fab',
    placement: 'left',
    icon: 'forum',
    titleKey: 'DMS.TOUR.ASSISTANT_TITLE',
    bodyKey: 'DMS.TOUR.ASSISTANT_BODY'
  },
  {
    id: 'help',
    target: '.aca-shell-header__help',
    placement: 'bottom',
    icon: 'help_outline',
    titleKey: 'DMS.TOUR.HELP_TITLE',
    bodyKey: 'DMS.TOUR.HELP_BODY'
  }
];

/** Bump to show the tour again to everyone, once, after it changes a lot. */
const DONE_KEY = 'aca.tour.v1.done.';

/**
 * The first-use tour: which step is showing, and whether this person has
 * already been through it.
 *
 * "Already been through it" is kept in the browser, per user. It only decides
 * whether the tour offers itself; the help menu starts it again any time.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingService {
  readonly active = signal(false);
  readonly steps = signal<TourStep[]>([]);
  readonly index = signal(0);
  readonly step = computed<TourStep | null>(() => this.steps()[this.index()] ?? null);
  readonly last = computed(() => this.index() >= this.steps().length - 1);

  private user = '';

  /** Starts from the first step, leaving out any whose target is not on screen for this person. */
  start(user = this.user, root: ParentNode = document): void {
    this.user = user;
    this.steps.set(TOUR_STEPS.filter((s) => !s.target || OnboardingService.shown(s.target, root)));
    this.index.set(0);
    this.active.set(true);
  }

  next(): void {
    if (this.last()) {
      this.finish();
    } else {
      this.index.update((i) => i + 1);
    }
  }

  back(): void {
    this.index.update((i) => Math.max(0, i - 1));
  }

  /** Ends the tour, whether finished or skipped; either way it does not offer itself again. */
  finish(): void {
    this.active.set(false);
    if (this.user) {
      try {
        localStorage.setItem(DONE_KEY + this.user.toLowerCase(), new Date().toISOString());
      } catch {
        // storage refused: the tour offers itself again next time, nothing worse
      }
    }
  }

  /** Whether this person has not been through the tour in this browser. */
  isNew(user: string): boolean {
    if (!user) {
      return false;
    }
    try {
      return !localStorage.getItem(DONE_KEY + user.toLowerCase());
    } catch {
      // no storage to remember an answer in: better not to show it every visit
      return false;
    }
  }

  /** An element that is in the page and takes up room — not one hidden by CSS or a collapsed panel. */
  static shown(selector: string, root: ParentNode = document): boolean {
    const el = root.querySelector(selector);
    if (!(el instanceof HTMLElement)) {
      return false;
    }
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }
}
